import { useBookingsUpdateMany, useBookingTemplateUpdate, useBookingUpdate } from "PFCore/hooks/queries";
import { useBookingTemplateCreate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template_create";
import { useBookingCreate } from "PFCore/hooks/queries/bookings/use_booking_create";
import { Booking } from "PFTypes";

import { GroupBookingsQuestionsOption } from "../../booking_questions/group_bookings_questions.types";
import { BookingFormMode } from "../booking_form.types";
import { BookingFormValues } from "../booking_form_values.types";
import { BookingType } from "../booking_type";
import { useBookingPayload } from "./use_booking_payload";
import { BookingResponse } from "./use_handle_submit";

type UseSubmitUpdate = {
  mode: BookingFormMode;
  initialBooking?: Booking;
};
type UseSubmitUpdateReturn = (
  values: BookingFormValues,
  dirtyBookingsIndexes?: number[]
) => Promise<BookingResponse[]>;

export const useSubmitUpdate = ({ mode, initialBooking }: UseSubmitUpdate): UseSubmitUpdateReturn => {
  const { update: updateBooking } = useBookingUpdate();
  const { update: updateBookingsMany } = useBookingsUpdateMany();
  const { update: updateBookingTemplate } = useBookingTemplateUpdate();
  const { create: createBooking } = useBookingCreate();
  const { create: createBookingTemplate } = useBookingTemplateCreate();

  const {
    getPayloadForCreateSingle,
    getPayloadForCloneSingle,
    getPayloadsForEditGroupBookingsOfSingleType,
    getPayloadForEditMultipleBookingsOfSingleType,
    getPayloadsForCreateBookingTemplate,
    getPayloadsForEditBookingTemplate
  } = useBookingPayload({ initialBooking });

  return (values: BookingFormValues, dirtyBookingsIndexes?: number[]): Promise<BookingResponse[]> => {
    const { bookingType } = values;
    switch (mode) {
      case BookingFormMode.EditSingle:
      case BookingFormMode.EditAll: {
        const { updateGroupOptionSelected } = values;
        const isUpdatingAllGroup =
          updateGroupOptionSelected === GroupBookingsQuestionsOption.GroupAll &&
          !!initialBooking?.booking_group_id;
        if (bookingType === BookingType.Repeated) {
          const payloads = getPayloadsForEditBookingTemplate({ values, isUpdatingAllGroup });
          return Promise.all(payloads.map((payload) => updateBookingTemplate(payload)));
        }
        // in case of updating the booking for all group, we need to use single booking update endpoint
        // in case of updating bookings not for a group, we can use update_many endpoint
        if (isUpdatingAllGroup) {
          const payloads = getPayloadsForEditGroupBookingsOfSingleType({
            values,
            bookingGroupId: initialBooking.booking_group_id
          });
          return Promise.all(payloads.map((payload) => updateBooking(payload)));
        } else {
          const payload = getPayloadForEditMultipleBookingsOfSingleType({
            values,
            mode,
            dirtyBookingsIndexes
          });
          return updateBookingsMany(payload).then(({ entries }) => entries);
        }
      }
      case BookingFormMode.Create: {
        if (bookingType === BookingType.Repeated) {
          const payloads = getPayloadsForCreateBookingTemplate({ values });
          return Promise.all(payloads.map((payload) => createBookingTemplate(payload)));
        } else {
          const payload = getPayloadForCreateSingle({ values });
          return createBooking(payload);
        }
      }
      case BookingFormMode.CloneSingle:
      case BookingFormMode.CloneAll: {
        const payload = getPayloadForCloneSingle({ values });
        return createBooking(payload);
      }
    }
  };
};
